import type { NikolaActivity } from "@chessla/shared";
import type { AnalyzedMoveResult } from "@/shared/api/engine.api";
import type { AnalysisProgress } from "@/features/analysis/types";
import type { ExplainMoveAction } from "@/features/board/review/explain-move.api";
import { type PendingAction, type SendMessageResult } from "@/features/chat/messaging/chat.api";
import type { ChatStoreMessage } from "@/features/chat/messaging/chat.store";
import type { NikolaMessageInput as SendMessageInput } from "@/features/chat/messaging/chat-contracts";
import { completeNikolaActivityParts } from "@/features/chat/messaging/chat-message-parts";
import { parseBoardMarkers } from "@/features/chat/board/parse-board-markers";
import { recordedMovePly } from "../review-decision";

export const shouldFollowEmbeddedThread = (nearBottom: boolean, submitted: boolean): boolean => submitted || nearBottom;

const gameNikolaResponseMessage = (result: SendMessageResult): ChatStoreMessage => {
  const parsed = parseBoardMarkers(result.reply);
  return {
    role: "assistant",
    content: result.reply,
    parts: result.parts,
    segments: parsed.segments,
    warnings: parsed.warnings.length ? parsed.warnings : undefined,
    boards: parsed.boards.length ? parsed.boards : undefined,
    pendingAction: result.pendingAction ?? null,
    creditsCharged: result.creditsCharged,
  };
};

export const gameNikolaResponseMessages = (result: SendMessageResult): ChatStoreMessage[] => {
  if (result.status !== "complete" || !result.reply.trim()) return [];
  return [gameNikolaResponseMessage(result)];
};

export const replaceGameNikolaWorkingMessage = (
  current: readonly ChatStoreMessage[],
  replacement: readonly ChatStoreMessage[],
): ChatStoreMessage[] => {
  const [answer, ...rest] = replacement;
  if (!answer) return current.slice(0, -1);
  const working = current.at(-1);
  const progress = working?.role === "assistant" ? completeNikolaActivityParts(working.parts) : [];
  return [...current.slice(0, -1), { ...answer, parts: [...progress, ...(answer.parts ?? [])] }, ...rest];
};

export const isEmbeddedBrowserAction = (action: PendingAction, gameId: string): boolean =>
  (action.type === "browser_engine" && Boolean(action.request)) ||
  (action.type === "start_game_analysis" && action.gameId === gameId);

const actionRequest: Record<ExplainMoveAction, string> = {
  why_mistake: "Explain why it failed and show the shortest verified consequence.",
  what_to_play: "Show the stronger idea and explain why it works.",
  fit_patterns: "Check whether this decision matches a recurring same-format pattern.",
  opponent_mistake: "Explain the chance my opponent gave me and how I could use it.",
  opponent_move: "Explain what this opponent move changed for me and why it helped or hurt.",
  missed_opportunity: "Explain the opportunity I missed and the cue that should have revealed it.",
  position_plan: "Explain the practical plan from this position and its first concrete step.",
  time_decision: "Check the clock evidence and explain whether time pressure affected this decision.",
};

const reviewMoveIdentity = (move: AnalyzedMoveResult): string => {
  const san = move.san.replace(/\s+/g, " ").trim().slice(0, 24);
  const ply = recordedMovePly(move);
  return `MOVE ${move.moveNumber}${ply == null ? "" : ` · PLY ${ply}`} · ${san} · ${move.isPlayerMove ? "MY MOVE" : "OPPONENT MOVE"}`;
};

export const reviewActionPrompt = (action: ExplainMoveAction, move: AnalyzedMoveResult): string =>
  `Review ${reviewMoveIdentity(move)}. ${actionRequest[action]} Use verified evidence from this selected game, distinguish facts from practical advice, and keep the answer focused.`;

export const reviewQuestionSubmission = (question: string): { request: string; retryDraft: string } => {
  const retryDraft = question.trim();
  return { request: retryDraft, retryDraft };
};

export const reviewMoveContext = (
  gameId: string,
  move: AnalyzedMoveResult | null,
): NonNullable<SendMessageInput["context"]> => {
  const ply = move ? recordedMovePly(move) : null;
  return {
    gameId,
    ...(move && ply
      ? {
          moveNumber: move.moveNumber,
          movePerspective: move.isPlayerMove ? ("player" as const) : ("opponent" as const),
          ply,
        }
      : {}),
  };
};

export const reviewQuestionScope = (
  gameId: string,
  pinnedMove: AnalyzedMoveResult | null,
): Pick<SendMessageInput, "context"> => ({ context: reviewMoveContext(gameId, pinnedMove) });

export const analysisActivityForProgress = (progress: AnalysisProgress): NikolaActivity => {
  const ratio = progress.totalMoves > 0 ? Math.min(1, Math.max(0, progress.currentMove / progress.totalMoves)) : 0;
  const [start, span] =
    progress.phase === "analyzing"
      ? [0, 70]
      : progress.phase === "alternatives"
        ? [70, 15]
        : progress.phase === "continuations"
          ? [85, 14]
          : progress.phase === "verifying"
            ? [99, 0]
            : [100, 0];
  const phase =
    progress.phase === "analyzing"
      ? "READING EVERY POSITION"
      : progress.phase === "alternatives"
        ? "VERIFYING BETTER MOVES"
        : progress.phase === "continuations"
          ? "BUILDING VERIFIED LINES"
          : progress.phase === "verifying"
            ? "VERIFYING TACTICS"
            : "ANALYSIS READY";
  return {
    kind: "analysis_started",
    status: "running",
    label: "ANALYZING THIS GAME",
    detail: `${phase} · MOVE ${progress.currentMove}/${progress.totalMoves}${progress.currentSan ? ` · ${progress.currentSan}` : ""}`,
    progress: Math.round(start + span * ratio),
  };
};
